import React, { useContext, useEffect, useRef, useState } from "react";
import { Header as TableHeadings } from "../table";
import up_down_arrow from "src/img/bodyAndMind/up-down.png";
import { DataTable, DataTableContainer, FlexContainer, TextLabel, Toast } from "src/components";
import {
    SaveButton,
    DeleteButtons,
    AddButtonWrapper
} from "../style";
import { useTableHooks } from "../../hooks/useTableHooks";
import { ManageCategoryRows } from "../table/CategoryRows";
import { hideCategory, showCategory } from "../../api";
import { BodyAndMindContext } from "../../BodyAndMindContext";

export const ManageCategoriesTable = (props) => {
    const { toggleAddCategory } = useContext(BodyAndMindContext);
    const {
        data,
        manageCategory,
        setHiddenCategoryUuids,
        setPayload
    } = props;

    // states for draggable table
    const [rows, setRows] = useState(data);
    const [shownItems, setShownItems] = useState([]);
    const [draggingIndex, setDraggingIndex] = useState(null);
    const [hoveredIndex, setHoveredIndex] = useState(null);

    const SortableColumns = [
        "select", // Not an actual column, no effect
        "image_video", // Not an actual column, no effect
        "name",
        item => String(item.subcategoriesCount || 0),
        item => String(item.postsCount || 0),
        item => String(item.companyUuid || ""), // Visibility toggle
    ];
    const {
        selectedItem,
        handleMainCheckbox,
        toggleItemSelection,
        isItemSelected,
        handleSortColumn,
        handleDeleteSelectedItems,
    } = useTableHooks(data, SortableColumns);

    const headings = [
        {
            name: <img
                src={up_down_arrow}
                alt="Arrow up down"
                style={{ height: "20px", width: "20px" }}

            />,
            textAlign: "center"
        },
        { name: "Category Image/Video", textAlign: "left" },
        { name: "Category Name", textAlign: "left" },
        { name: "Content", textAlign: "left" },
        { name: "Subcategories", textAlign: "left" },
        { name: "Visibility", textAlign: "left" },
        { name: "View/Edit", textAlign: "left" },
        { name: "Hide/Show", textAlign: "left" }
    ];

    const dragItem = useRef(null);
    const dragOverItem = useRef(null);

    const handleDragStart = (index) => {
        dragItem.current = index;
        setDraggingIndex(index);
    };

    const handleDragEnter = (index) => {
        dragOverItem.current = index;
    };

    const handleDragOver = (index) => {
        setHoveredIndex(index);
    };

    const handleDragEnd = () => {
        const fromIndex = dragItem.current;
        const toIndex = dragOverItem.current;
        if (fromIndex === toIndex || toIndex === null) {
            setDraggingIndex(null);
            setHoveredIndex(null);
            return;
        }

        const updatedRows = [...rows];
        const draggedRow = updatedRows.splice(fromIndex, 1)[0];
        updatedRows.splice(toIndex, 0, draggedRow);
        setRows(updatedRows);
        setDraggingIndex(null);
        setHoveredIndex(null);
        dragItem.current = null;
        dragOverItem.current = null;

        setPayload(
            updatedRows.map((item, index) => ({
                ...item,
                order: index + rows[0].order, // adding the index based on first created category by a company.
            }))
        );
        setRows(updatedRows.map((item, index) => ({
            ...item,
            order: index + rows[0].order, // adding the index based on first created category by a company.
        })));
    };

    const isASortableColumn = (index) => index > 0;

    // Add all categories with deletedAt !== null to shownItems state on component mount
    useEffect(() => {
        const deleted = data.filter(item => item.deletedAt !== null);
        setShownItems(deleted);
    }, [data]);

    // Update rows when data changes
    useEffect(() => {
        setRows(data);
    }, [data]);

    const onShow = async (item) => {
        const isAlreadySelected = shownItems.some(q => q.uuid === item.uuid);

        try {
            if (isAlreadySelected) {
                await showCategory(item.uuid);
                Toast.success("Category shown successfully");
                const nextShownItems = shownItems.filter(q => q.uuid !== item.uuid);
                setShownItems(nextShownItems);
                setHiddenCategoryUuids(nextShownItems.map(q => q.uuid));
            } else {
                await hideCategory(item.uuid);
                Toast.success("Category hidden successfully");
                const nextShownItems = [...shownItems, item];
                setShownItems(nextShownItems);
                setHiddenCategoryUuids(nextShownItems.map(q => q.uuid));
            }
        } catch (error) {
            console.error(error);
            Toast.error(error.getApiErrorDetails().message);
        }
    };

    const isItemShow = (uuid) =>
        shownItems?.some(u => u.uuid == uuid);

    return (
        <FlexContainer width="auto" height="60vh">
            <FlexContainer direction="row" tDirection="row" alignItems="center">
                <TextLabel style={{ font: "normal normal bold 20px/24px Manrope", width: "100%", marginTop: 10 }}>Available Categories</TextLabel>
                <FlexContainer direction="row" gap="10px" justifyContent="space-between" width="100%">
                    {(selectedItem.length > 0) && (
                        <DeleteButtons
                            onClick={() => handleDeleteSelectedItems()}>
                            Delete Category
                        </DeleteButtons>
                    )}
                    <AddButtonWrapper>
                        <SaveButton
                            width="184px"
                            height="39px"
                            color="#FFFFFF"
                            background="006CFF"
                            border="2px solid #171A22"
                            onClick={() => toggleAddCategory()}
                        >
                            Create New Category
                        </SaveButton>
                    </AddButtonWrapper>
                </FlexContainer>
            </FlexContainer>

            <DataTableContainer style={{ width: "auto", marginTop: "1rem" }}>
                <DataTable minWidth="1040px">
                    <thead>
                        <TableHeadings
                            headings={headings}
                            onSort={handleSortColumn}
                            onMainCheck={handleMainCheckbox}
                            isSortable={isASortableColumn}
                        />
                    </thead>
                    <tbody>
                        <ManageCategoryRows
                            handleDragStart={handleDragStart}
                            handleDragEnter={handleDragEnter}
                            handleDragEnd={handleDragEnd}
                            handleDragOver={handleDragOver}
                            draggingIndex={draggingIndex}
                            hoveredIndex={hoveredIndex}

                            tableData={rows}
                            onToggle={(item) => toggleItemSelection(item)}
                            isSelected={isItemSelected}
                            onShow={onShow}
                            isItemShow={isItemShow}
                            manageCategory={manageCategory}
                        />
                    </tbody>
                </DataTable>
            </DataTableContainer>
        </FlexContainer>
    );
};
